import { AdaptiveDpr, AdaptiveEvents, BakeShadows, OrbitControls } from "@react-three/drei"
import { Canvas } from "@react-three/fiber"
import { EffectComposer, N8AO, SMAA } from '@react-three/postprocessing'
import * as THREE from "three"
import geojsonData from './assets/floorplan.geojson.json'
import { Environment } from "./Environment"
import { Geometry, Base, Addition, Subtraction } from '@react-three/csg'
import CreateCubeFromPolygon from "./CreateCubeFromPolygon"
import LabelComponent from "./LabelComponent"
import * as BufferGeometryUtils from 'three/addons/utils/BufferGeometryUtils.js';

export default function App() {


  const walls = [];
  const furniture = [];
  const doors = [];
  const doorHoles = [];
  const labels = [];
  const corners = [];

  // Check if the GeoJSON is valid and has features
  if (geojsonData && geojsonData.features) {
    geojsonData.features.forEach((feature, i) => {
      const mesh = CreateCubeFromPolygon(feature.geometry.coordinates)

      switch (feature.properties.type) {
        case 'wall':
          walls.push(mesh);
          break;
        case 'door':
          doors.push(mesh);
          break;
        case 'doorHole':
          doorHoles.push(mesh);
          break;
        case 'corner':
          corners.push(mesh);
          break;
        case 'originalWall':
          // do nothing
          break
        default:
          furniture.push(mesh);
          const positionAttribute = mesh.attributes.position;
          const boundingBox = new THREE.Box3().setFromBufferAttribute(positionAttribute);

          // Calculate the top center point of the bounding box
          const max = boundingBox.max;
          const min = boundingBox.min;
          const topCenter = new THREE.Vector3(
            (max.x + min.x) / 2, // Average x for center
            (max.y + min.y) / 2,  // Average y for center 
            max.z + 0.1, // Topmost z
          );

          labels.push({ point: topCenter, text: feature.properties.type });
          break;
      }
    });
  }

  // Calculate the centroid of all the polygons
  let extentCenter = new THREE.Vector3(0, 0, 0);
  let minPoint = new THREE.Vector3(Infinity, Infinity, Infinity);
  let maxPoint = new THREE.Vector3(-Infinity, -Infinity, -Infinity);

  walls.forEach((geometry) => {
    const positionAttribute = geometry.attributes.position;
    const boundingBox = new THREE.Box3().setFromBufferAttribute(positionAttribute);

    // Update min and max points
    minPoint.min(boundingBox.min);
    maxPoint.max(boundingBox.max);
  });

  // Calculate the center of the extent
  extentCenter.addVectors(minPoint, maxPoint).multiplyScalar(0.5);

  // Calculate the lowest z value
  extentCenter.z = walls.reduce((acc, geometry) => {
    const positionAttribute = geometry.attributes.position;
    const boundingBox = new THREE.Box3().setFromBufferAttribute(positionAttribute);
    return boundingBox.min.z < acc ? boundingBox.min.z : acc
  }, 0);

  // Calculate the angle of the principal direction
  const firstPoint = geojsonData.features[0].geometry.coordinates[0]
  const secondPoint = geojsonData.features[0].geometry.coordinates[1]

  const px = firstPoint[0] - secondPoint[0]
  const py = firstPoint[1] - secondPoint[1]

  const pVector = new THREE.Vector3(px, py, 0); // Replace px, py, pz with your PCA results
  const xVector = new THREE.Vector3(1, 0, 0);   // Vector [1,0,0] in Three.js
  const cosTheta = pVector.dot(xVector) / (pVector.length() * xVector.length());   // Calculate the cosine of the angle using the dot product and magnitudes
  const thetaRadians = Math.acos(cosTheta);   // Calculate the angle in radians


  const groupCenter = extentCenter;
  const groupRotation = thetaRadians;

  // merge geometries for better performance
  const wallsMerged = BufferGeometryUtils.mergeBufferGeometries([...walls, ...corners]);
  const furnitureMerged = BufferGeometryUtils.mergeBufferGeometries(furniture);
  const doorsMerged = BufferGeometryUtils.mergeBufferGeometries(doors);
  const doorHolesMerged = BufferGeometryUtils.mergeBufferGeometries(doorHoles);


  const materialWall = new THREE.MeshStandardMaterial({ side: THREE.FrontSide })
  materialWall.color = new THREE.Color("white");

  const materialGlass = new THREE.MeshStandardMaterial({ side: THREE.FrontSide })
  materialGlass.color = new THREE.Color("MidnightBlue");
  materialGlass.transparent = true;
  materialGlass.opacity = 0.2;
  materialGlass.refractionRatio = 0.98;
  materialGlass.reflectivity = 0.9;


  return (
    <Canvas
      performance={{ min: 0.5 }}
      frameloop="demand"
      orthographic
      gl={{
        antialias: false,
        toneMapping: THREE.ACESFilmicToneMapping,
        outputColorSpace: THREE.LinearSRGBColorSpace
      }}
      camera={{
        zoom: 50,
        position: [100, 100, 100]
      }}
      shadows
    >
      <color attach="background" args={["#dfdfdf"]} />
      <Environment />
      <OrbitControls />
      <AdaptiveEvents />
      <AdaptiveDpr pixelated />
      <BakeShadows />
      <group rotation-x={-Math.PI / 2} rotation-z={-Math.PI / 2} >
        <mesh castShadow receiveShadow material={materialWall}  >
          <boxGeometry args={[15, 15, 0.5]} />
        </mesh>
        <group rotation-z={groupRotation}>
          <group position={[-groupCenter.x, -groupCenter.y, -groupCenter.z]}>
            <mesh castShadow receiveShadow>
              <Geometry useGroups>
                <Base>
                  <boxGeometry args={[0.01, 0.01, 0.01]} />
                </Base>
                <Addition   >
                  <Geometry useGroups >
                    <Base name="base" >
                      <boxGeometry args={[0.01, 0.01, 0.01]} />
                    </Base>
                    <Addition geometry={wallsMerged} material={materialWall} />
                    <Subtraction geometry={doorHolesMerged} material={materialWall} />
                    <Addition geometry={furnitureMerged} material={materialWall} />
                    <Addition geometry={doorsMerged} material={materialGlass} />
                  </Geometry>
                </Addition>

              </Geometry>
            </mesh>
          </group>
        </group>
        <group rotation-z={groupRotation}>
          <group position={[-groupCenter.x, -groupCenter.y, -groupCenter.z]}  >
            {labels.map((label, index) => (
              <LabelComponent key={index} position={label.point} text={label.text} />
            ))}
          </group>
        </group>
      </group>
      <EffectComposer multisampling={false} autoClear={false}>
        <N8AO halfRes color="black" aoRadius={2} intensity={1} aoSamples={6} denoiseSamples={4} />
        <SMAA />
      </EffectComposer>
    </Canvas >

  )
}
